/**
 * Bounded Context: Initiative — Pinia store
 *
 * Mantém o estado de contexto para `decideInitiative`. O store é thin
 * (a lógica está em ui-constraints.ts, pura e testável).
 *
 * HLOS-114: imports atualizados. `Initiative` agora vem de `./ui-constraints`
 * (TS-only, NÃO do codegen — diferente de `InitiativeDecision` que vem de
 * `./generated`). `InitiativeContext` também vem de `./ui-constraints`.
 */

import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  decideInitiative,
  decideInitiativeOrNull,
  type Initiative,
  type InitiativeContext,
} from "./ui-constraints";

export const useInitiativeStore = defineStore("initiative", () => {
  const userRequestedProvocation = ref(false);
  const hasCuriosityGaps = ref(false);
  const userInUi = ref(true);

  /** Contexto atual derivado dos refs. */
  const context = computed<InitiativeContext>(() => ({
    userRequestedProvocation: userRequestedProvocation.value,
    hasCuriosityGaps: hasCuriosityGaps.value,
    userInUi: userInUi.value,
  }));

  /** Decisão atual (computed — reativo). Sempre retorna um Initiative válido. */
  const current = computed<Initiative>(() => decideInitiative(context.value));

  /**
   * Decisão atual em forma `Initiative | null` (HLOS-19 AC).
   * `null` ⇔ "sem decisão ativa" (equivale a `kind: "none"`).
   */
  const currentOrNull = computed<Initiative | null>(() =>
    decideInitiativeOrNull(context.value),
  );

  /** `true` quando há iniciativa ativa visível (não silent). */
  const hasInitiative = computed<boolean>(() => currentOrNull.value !== null);

  /** Actions de mutação (cada uma atualiza um flag específico). */
  function setUserInUi(v: boolean): void {
    userInUi.value = v;
  }
  function setUserRequestedProvocation(v: boolean): void {
    userRequestedProvocation.value = v;
  }
  function setHasCuriosityGaps(v: boolean): void {
    hasCuriosityGaps.value = v;
  }

  /**
   * Recusa iniciativa atual (R3: ask → learner).
   * HLOS-40 — CC-05: zero penalidade. Apenas limpa flag de provocação
   * aceita; próxima decisão retorna a silent automaticamente.
   */
  function refuseInitiative(): void {
    userRequestedProvocation.value = false;
  }

  /**
   * Modifica iniciativa com input do usuário.
   * HLOS-40 — registra que o usuário quer explorar com ângulo customizado.
   * Não bloqueia próxima decisão (R4: explain_reasoning → shared).
   */
  function modifyInitiative(userInput: string): void {
    // Validação: input não-vazio (zero manipulação por omissão)
    if (userInput.trim().length === 0) return;
    userRequestedProvocation.value = true;
    // Nota: em v0.2 a modificação apenas registra intenção (persistência em HLOS-7 vault).
  }

  return {
    userRequestedProvocation,
    hasCuriosityGaps,
    userInUi,
    current,
    currentOrNull,
    hasInitiative,
    setUserInUi,
    setUserRequestedProvocation,
    setHasCuriosityGaps,
    refuseInitiative,
    modifyInitiative,
  };
});
